export class PhoneNumberUtils {
  /**
   * Masks a phone number, preserving the country code (if present) and the last four digits.
   * The masked format will be similar to "X *** *** XX-XX" where:
   * - "X" represents the preserved starting digits (country code or initial digits)
   * - "*** ***" represents the masked middle digits
   * - "XX-XX" represents the preserved last four digits
   *
   * Examples:
   *   Input: "+71234567890"
   *   Output: "+7 *** *** 7890"
   *
   *   Input: "1234567890"
   *   Output: "1 *** *** 7890"
   *
   *   Input: "+1 234 567 8901"
   *   Output: "+1 *** *** 8901"
   *
   * @param phone - The phone number string to be masked.
   * @returns The masked phone number.
   * @throws Will throw an error if the phone number format is invalid or too short.
   */
  static maskPhoneNumberFlexible(phone?: string): string {
    if (!phone) {
      return '';
    }
    // Remove any non-digit and non-plus characters for standardization
    const standardizedPhone = phone.replace(/[^\d+]/g, '');

    // Determine if the phone number starts with a '+'
    const hasPlus = standardizedPhone.startsWith('+');

    // Remove the '+' for processing, but keep it for the masked output if present
    const digitsOnly = hasPlus ? standardizedPhone.slice(1) : standardizedPhone;

    // Validate that the phone number has at least 7 digits to allow masking
    // (Adjust this based on your specific requirements)
    if (digitsOnly.length < 7) {
      throw new Error('Invalid phone number format. Phone number is too short to mask.');
    }

    // Extract the country code or initial digits
    // For simplicity, we'll preserve the first 1-3 digits as the "country code"
    // Adjust the number of preserved digits based on your requirements
    let preservedStart: string;
    let startIndex: number;

    if (hasPlus) {
      // If there's a plus, preserve the first digit after '+'
      preservedStart = '+' + digitsOnly.slice(0, 1);
      startIndex = 1;
    } else {
      // If no plus, preserve the first digit
      preservedStart = digitsOnly.slice(0, 1);
      startIndex = 1;
    }

    // Determine how many digits to mask
    const totalDigits = digitsOnly.length;
    const digitsToMask = totalDigits - startIndex - 4; // Preserve last 4 digits

    if (digitsToMask < 0) {
      throw new Error('Invalid phone number format. Not enough digits to mask.');
    }

    // Create the masked middle part
    // For better readability, group the masked digits (e.g., "*** ***")
    const maskedMiddle = '*'.repeat(digitsToMask);
    // Optionally, insert spaces or other separators in the masked middle
    // Here, we'll group every 3 masked digits for readability
    const groupedMaskedMiddle = maskedMiddle.replace(/(.{3})/g, '$1 ').trim();

    // Extract the last four digits
    const lastFourDigits = digitsOnly.slice(-4);
    // Format the last four digits as "XX-XX"
    const formattedLastFour = `${lastFourDigits.slice(0, 2)}-${lastFourDigits.slice(2)}`;

    // Combine all parts
    const maskedPhone = `${preservedStart} ${groupedMaskedMiddle} ${formattedLastFour}`;

    return maskedPhone;
  }

  static removeSpacesAndBrackets(input: string): string {
    // Use a regular expression to replace spaces, parentheses, and other brackets
    return input.replace(/[()\s]/g, '');
  }

  static removeSpecialCharacters(input: string): string {
    // Use a regular expression to replace spaces, parentheses, and other brackets
    return input.replace(/[+()\s]/g, '');
  }
}
